import React from 'react';
import { 
  DollarSign, TrendingUp, TrendingDown, PieChart, Lightbulb, Info 
} from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';
import { TrendIndicator, ProgressBar, CollapsibleSection } from './ReportComponents';

const ProfitLossReport = ({ 
  data, 
  expandedSections, 
  toggleSection,
  excludePersonal = true,
  onExcludePersonalChange 
}) => {
  if (!data) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No data available for this period.</p>
      </div>
    );
  }

  const handleFilterChange = (checked) => {
    if (onExcludePersonalChange) {
      onExcludePersonalChange(checked);
    }
  };

  return (
    <div className="space-y-4">
      {/* NEW: Transaction Type Filter */}
      <div className="card p-3 border-purple-200 bg-purple-50 print:hidden">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={excludePersonal}
            onChange={(e) => handleFilterChange(e.target.checked)}
            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
          />
          <div>
            <span className="text-sm font-medium text-purple-900">
              Exclude Personal/Free services from P&L
            </span>
            <p className="text-xs text-purple-700">
              Personal/Free services are tracked for records but don't count as taxable business income
            </p>
          </div>
        </label>
        
        {!excludePersonal && data.personalServicesValue > 0 && (
          <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
            💡 Including {formatCurrency(data.personalServicesValue)} in Personal/Free services in this view for reference
          </div>
        )}
      </div>

      {/* Period Label & Trend */}
      {data.periodLabel && (
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{data.periodLabel}</h2>
          {data.revenueChangePercent !== null && (
            <div className="text-sm text-gray-500">
              vs {data.comparisonPeriodLabel}: <TrendIndicator value={data.revenueChangePercent} />
            </div>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="card p-0 overflow-hidden">
        {/* Mobile: Stacked */}
        <div className="sm:hidden divide-y divide-gray-100">
          <div className="flex items-center justify-between p-3 bg-green-50">
            <div className="flex items-center gap-2">
              <TrendingUp size={16} className="text-green-600" />
              <span className="text-sm font-medium text-green-700">
                {excludePersonal ? 'Business Revenue' : 'Total Revenue'}
              </span>
            </div>
            <div className="text-right">
              <p className="font-bold text-green-900">{formatCurrency(data.grossRevenue)}</p>
              <p className="text-xs text-green-600">{data.invoicesPaid} invoices</p>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-red-50">
            <div className="flex items-center gap-2">
              <TrendingDown size={16} className="text-red-600" />
              <span className="text-sm font-medium text-red-700">Expenses</span>
            </div>
            <div className="text-right">
              <p className="font-bold text-red-900">{formatCurrency(data.totalExpenses)}</p>
              <p className="text-xs text-red-600">{data.expenseCount} expenses</p>
            </div>
          </div>
          <div className={`flex items-center justify-between p-3 ${data.netProfit >= 0 ? 'bg-blue-50' : 'bg-amber-50'}`}>
            <div className="flex items-center gap-2">
              <DollarSign size={16} className={data.netProfit >= 0 ? 'text-blue-600' : 'text-amber-600'} />
              <span className={`text-sm font-medium ${data.netProfit >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>Net Profit</span>
            </div>
            <div className="text-right">
              <p className={`font-bold ${data.netProfit >= 0 ? 'text-blue-900' : 'text-amber-900'}`}>
                {formatCurrency(data.netProfit)}
              </p>
              <p className={`text-xs ${data.netProfit >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                {data.profitMargin.toFixed(1)}% margin
              </p>
            </div>
          </div>
        </div>

        {/* Desktop: Side by side */}
        <div className="hidden sm:grid sm:grid-cols-3 sm:divide-x divide-gray-100">
          <div className="p-4 bg-green-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">
                  {excludePersonal ? 'Business Revenue' : 'Total Revenue'}
                </p>
                <p className="text-2xl font-bold text-green-900">{formatCurrency(data.grossRevenue)}</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs text-green-600">{data.invoicesPaid} invoices</p>
                  {data.revenueChangePercent !== null && (
                    <TrendIndicator value={data.revenueChangePercent} />
                  )}
                </div>
              </div>
              <TrendingUp className="text-green-500" size={28} />
            </div>
          </div>
          <div className="p-4 bg-red-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700">Total Expenses</p>
                <p className="text-2xl font-bold text-red-900">{formatCurrency(data.totalExpenses)}</p>
                <p className="text-xs text-red-600 mt-1">{data.expenseCount} expenses</p>
              </div>
              <TrendingDown className="text-red-500" size={28} />
            </div>
          </div>
          <div className={`p-4 ${data.netProfit >= 0 ? 'bg-blue-50' : 'bg-amber-50'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${data.netProfit >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>Net Profit</p>
                <p className={`text-2xl font-bold ${data.netProfit >= 0 ? 'text-blue-900' : 'text-amber-900'}`}>
                  {formatCurrency(data.netProfit)}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <p className={`text-xs ${data.netProfit >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                    {data.profitMargin.toFixed(1)}% margin
                  </p>
                  {data.profitChangePercent !== null && (
                    <TrendIndicator value={data.profitChangePercent} />
                  )}
                </div>
              </div>
              <DollarSign className={data.netProfit >= 0 ? 'text-blue-500' : 'text-amber-500'} size={28} />
            </div>
          </div>
        </div>
      </div>

      {/* Personal Services Reference Section - Always show if there are any */}
      {data.personalServicesValue > 0 && (
        <div className="bg-gradient-to-r from-purple-50 to-violet-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <Info size={20} className="text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-purple-900">Personal & Family Work</h3>
                <p className="text-sm text-purple-700">
                  {excludePersonal ? 'Tracked separately (not in P&L above)' : 'Included in business revenue above'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-purple-900">
                {formatCurrency(data.personalServicesValue)}
              </div>
              <div className="text-xs text-purple-600">
                {data.personalServicesCount} job{data.personalServicesCount !== 1 ? 's' : ''} • $0 charged
              </div>
            </div>
          </div>
          
          {/* Impact Summary — grossRevenue already includes personalServicesValue
             when excludePersonal is false, so don't add it again here */}
          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-purple-200">
            <div className="text-center">
              <div className="text-sm font-medium text-purple-900">
                {formatCurrency((data.grossRevenue || 0) + (excludePersonal ? (data.personalServicesValue || 0) : 0))}
              </div>
              <div className="text-xs text-purple-600">Total Work Value</div>
              <div className="text-[10px] text-purple-400">Billed + Personal/Free combined</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-medium text-purple-900">
                {(() => {
                  const totalWorkValue = (data.grossRevenue || 0) + (excludePersonal ? (data.personalServicesValue || 0) : 0);
                  return totalWorkValue > 0
                    ? ((data.personalServicesValue / totalWorkValue) * 100).toFixed(0)
                    : 0;
                })()}%
              </div>
              <div className="text-xs text-purple-600">Given for Free</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-medium text-purple-900">
                {data.personalServicesCount > 0 
                  ? formatCurrency(data.personalServicesValue / data.personalServicesCount)
                  : formatCurrency(0)}
              </div>
              <div className="text-xs text-purple-600">Avg per Job</div>
            </div>
          </div>
        </div>
      )}

      {/* Tax Set-Aside Recommendation */}
      {data.recommendedTaxSetAside > 0 && (
        <div className="card p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Lightbulb size={20} className="text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-purple-900">Tax Set-Aside Recommendation</h3>
              <p className="text-sm text-purple-700 mt-1">
                Based on your actual taxable net profit, consider setting aside approximately{' '}
                <span className="font-bold">{formatCurrency(data.recommendedTaxSetAside)}</span>{' '}
                (~{data.taxSetAsidePercent?.toFixed(0)}%) for estimated taxes.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="card p-3">
          <p className="text-xs text-gray-500">Outstanding</p>
          <p className="text-lg font-bold text-amber-600">{formatCurrency(data.outstandingReceivables)}</p>
          <p className="text-xs text-gray-400">Unpaid invoices</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500">Scheduled</p>
          <p className="text-lg font-bold text-indigo-600">{formatCurrency(data.scheduledRevenue)}</p>
          <p className="text-xs text-gray-400">Upcoming jobs</p>
        </div>
        {data.averageJobRevenue > 0 && (
          <div className="card p-3">
            <p className="text-xs text-gray-500">Avg per Job</p>
            <p className="text-lg font-bold text-green-600">{formatCurrency(data.averageJobRevenue)}</p>
            <p className="text-xs text-gray-400">Revenue/job</p>
          </div>
        )}
        {data.totalDeductibleExpenses > 0 && (
          <div className="card p-3">
            <p className="text-xs text-gray-500">Deductible</p>
            <p className="text-lg font-bold text-teal-600">{formatCurrency(data.totalDeductibleExpenses)}</p>
            <p className="text-xs text-gray-400">Tax deductions</p>
          </div>
        )}
      </div>

      {/* Expenses by Category - Collapsible */}
      <CollapsibleSection
        title="Expenses by Category"
        icon={PieChart}
        isExpanded={expandedSections.expenses}
        onToggle={() => toggleSection('expenses')}
        count={data.expensesByCategory.length}
      >
        {data.expensesByCategory.length === 0 ? (
          <p className="text-gray-500 text-center py-6 text-sm">No expenses recorded</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {data.expensesByCategory.map((cat, index) => (
              <div key={index} className="px-3 py-2 hover:bg-gray-50">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{cat.displayName}</p>
                  <p className="text-sm font-semibold text-gray-900">{formatCurrency(cat.total)}</p>
                </div>
                <div className="flex items-center justify-between">
                  <ProgressBar 
                    value={cat.total} 
                    max={data.totalExpenses} 
                    color="bg-red-400"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {cat.count} expense{cat.count !== 1 ? 's' : ''}
                  {cat.deductible !== cat.total && (
                    <span className="text-green-600 ml-2">• {formatCurrency(cat.deductible)} deductible</span>
                  )}
                </p>
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>
    </div>
  );
};

export default ProfitLossReport;